import React, { useCallback, useState } from "react";
import {
  IonContent,
  IonPage,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonToast,
  IonCard,
  IonImg,
  IonGrid,
  IonRow,
  IonCol,
  IonFooter,
} from "@ionic/react";
import "./Login.css";
import { generateOTP, isUserRegistered, login } from "../services/user.service";
import { handleErrors, setAccessToken } from "../services/common.service";

interface ComponentProps {
  connected: boolean;
  onSuccess: Function;
}

export const isValidMobile = (mobile: string) => /^\d{7,15}$/.test(mobile);

const Login: React.FC<ComponentProps> = (props: ComponentProps) => {
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpGenerated, setOtpGenerated] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [otpMsg, setOtpMsg] = useState("");

  const checkUserRegistered = async () => {
    try {
      const isMobileNumber = isValidMobile(userName);
      const payload = isMobileNumber
        ? { mobile: userName }
        : { userId: userName };
      const response = await isUserRegistered(payload);
      return response;
    } catch (err: any) {
      console.error("Error checking if user is registered:", err);
      return false;
    }
  };

  const sendOTPRequest = useCallback(async () => {
    setOtpMsg("");
    setIsOTPLoading(true);
    try {
      const userRegistered = await checkUserRegistered();

      if (!!userRegistered.registered) {
        try {
          // Generate OTP if user is registered
          await generateOTP(userRegistered.farmUserId);
          setOtpGenerated(true);
          setUserId(userRegistered.farmUserId);
          setOtpMsg("OTP generated successfully");
        } catch (otpError: any) {
          setOtpError("Failed to generate OTP. Please try again.");
        }
      } else {
        setOtpError("User not registered. Please check your credentials.");
      }
    } catch (err: any) {
      console.error("Error in OTP generation:", err);
      setOtpError(err.message || "Failed to generate OTP");
    } finally {
      setIsOTPLoading(false);
    }
  }, [userName]);

  const sendLoginRequest = useCallback(async () => {
    setIsLoginLoading(true);
    const credentials = {
      farmUserId: userId || userName,
      otp: otp,
    };
    try {
      const data = await login(credentials).then(handleErrors);
      setAccessToken(data.access_token);
      localStorage.setItem("farmUserId", data.farmUserId);
      props.onSuccess(true);
    } catch (err: any) {
      setLoginError(err);
      setIsLoginLoading(false);
    }
  }, [userName, otp]);

  return (
    <IonPage>
      <IonContent fullscreen>
        <IonCard class="login-card">
          <IonGrid>
            <IonRow>
              <IonCol>
                <IonImg class="logo" src="assets/logo.png" />
              </IonCol>
              <IonCol>
                <h1 className="title">Fyllo</h1>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <h3 className="sub-title">
                  Where technology meets agriculture
                </h3>
              </IonCol>
            </IonRow>
            <IonRow class="login-content">
              <IonCol>
                <IonList>
                  {!otpGenerated && (
                    <IonItem>
                      <IonLabel>UserID</IonLabel>
                      <IonInput
                        id="userName"
                        name="userName"
                        value={userName}
                        placeholder="UserId / Mobile Number"
                        onIonChange={(e) => setUserName(e.detail.value!)}
                      ></IonInput>
                    </IonItem>
                  )}

                  {otpGenerated && (
                    <IonItem>
                      <IonLabel>OTP</IonLabel>
                      <IonInput
                        id="otp"
                        name="otp"
                        type="password"
                        value={otp}
                        placeholder="Enter OTP"
                        onIonChange={(e) => setOtp(e.detail.value!)}
                      ></IonInput>
                    </IonItem>
                  )}

                  <div className="button-panel">
                    {otpGenerated && (
                      <IonButton
                        expand="block"
                        disabled={isLoginLoading}
                        onClick={sendLoginRequest}
                      >
                        {isLoginLoading ? "Logging in..." : "Login"}
                      </IonButton>
                    )}

                    <IonButton
                      expand="block"
                      disabled={
                        isOTPLoading ||
                        (!otpGenerated &&
                          (!userName || userName.trim().length === 0))
                      }
                      color={otpGenerated ? "medium" : "primary"}
                      onClick={sendOTPRequest}
                    >
                      {isOTPLoading ? "Please wait..." : (otpGenerated ? "Re-generate" : "Generate") + " OTP"}
                    </IonButton>
                  </div>
                </IonList>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCard>

        <IonToast
          isOpen={!!otpMsg}
          onDidDismiss={() => setOtpMsg("")}
          message={otpMsg}
          duration={2000}
          color="success"
        />

        <IonToast
          isOpen={!!otpError}
          onDidDismiss={() => setOtpError("")}
          message={otpError}
          duration={2000}
          color="danger"
        />

        <IonToast
          isOpen={!!loginError}
          onDidDismiss={() => setLoginError("")}
          message={loginError}
          duration={2000}
          color="danger"
        />
      </IonContent>
      <IonFooter>
        {!props.connected && (
          <IonButton color="danger" expand="block">
            Not connected to Internet
          </IonButton>
        )}
      </IonFooter>
    </IonPage>
  );
};

export default Login;
